import Vue from 'vue';
import {
  CommunicationType,
  ConversationSummary,
  UiMessage
} from "@/components/communications/CommunicationInterfaces";
import MessageColumnMixin from "@/components/communications/cards/mixins/MessageColumnMixin";
import FilterComponent from "@/componentsReusable/FilterComponent.vue";
import * as communicationsFilterEnums from "@/enums/communicationsFilterEnums";
import TitleDropdownPicker from "@/componentsReusable/TitleDropdownPicker.vue";
import {ViewStateType} from "@/enums/ViewStateType";
import Personalization from "@/components/communications/components/Personalization.vue";
import {ContactType} from "@/components/communications/models/contactModel";
import debounce from 'lodash.debounce';
import {getProviderStatusIndicator} from "@/components/providers/ProviderSearchResultModel";

export default Vue.component('message-inbox-card', {
  name: 'MessageInboxCard',
  props: {
    contactType: { type: Object },
    providerId: { type: Number },
    providerName: { type: String },
    facilityContactId: { type: Number },
    facilityContactName: { type: String },
    dropCount :Number
  },
  data () {
    return {
      checkboxAllMessages: false,
      messageSearchText: '',
      showCampaigns: false,
      searchInput: '',
      loadingSearchResults: false,
      filtersSelected: [] as any[],
      campaignOptions: [] as any[],
      selectedCampaign: '...',
      selectedCampaignId: 0,
      defaultFilters: [] as any[],
      newMessage: '',
      messageTypes: '',
      maxLength: 420,
      loadingInitialData: false,
      contactRowExpanded: false,
      filterTypes: {
        [communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE]: {
          id: communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE,
          name: "Team Member",
          isSelected: true,
          searchAPI: '/api/users/search?userNameLike=',
          conditionResponseData: function (responseData: any) {
            return responseData.map((responseObject: any) => {
              responseObject.filterTypeId = communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE;
              return responseObject;
            });
          }
        },
        [communicationsFilterEnums.STATUS_FILTER_TYPE]: {
          id: communicationsFilterEnums.STATUS_FILTER_TYPE,
          name: "Status",
          isSelected: false,
          searchEnum: communicationsFilterEnums.statuses
        },
      },
      providerFilterTypes: {
        [communicationsFilterEnums.PROVIDER_FILTER_TYPE]: {
          id: communicationsFilterEnums.PROVIDER_FILTER_TYPE,
          name: "Provider",
          isSelected: false,
          searchAPI: '/api/providers/search?excludeInactive=true&providerNameLike=',
          conditionResponseData: function (responseData: any) {
            return responseData.map((responseObject: any) => {
              responseObject.filterTypeId = communicationsFilterEnums.PROVIDER_FILTER_TYPE;
              const name = `${responseObject.firstName} ${responseObject.lastName}`;
              responseObject.name = responseObject.title ? name + ' | ' + responseObject.title : name;
              responseObject.prefixHtml = getProviderStatusIndicator(responseObject.statusId);
              responseObject.location = '';
              if (responseObject.city && responseObject.state) {
                responseObject.location = `${responseObject.city}, ${responseObject.state}`;
              }
              return responseObject;
            });
          }
        },
      },
      facilityFilterTypes: {
        [communicationsFilterEnums.FACILITY_CONTACT_FILTER_TYPE]: {
          id: communicationsFilterEnums.FACILITY_CONTACT_FILTER_TYPE,
          name: "Facility Contact",
          isSelected: false,
          searchAPI: '/api/facility-contact/search?facilityContactNameLike=',
          conditionResponseData: function (responseData: any) {
            return responseData.map((responseObject: any) => {
              responseObject.filterTypeId = communicationsFilterEnums.FACILITY_CONTACT_FILTER_TYPE;
              return responseObject;
            });
          }
        },
        [communicationsFilterEnums.FACILITY_FILTER_TYPE]: {
          id: communicationsFilterEnums.FACILITY_FILTER_TYPE,
          name: "Facility",
          isSelected: false,
          searchAPI: '/api/facilities/search?facilityNameLike=',
          conditionResponseData: function (responseData: any) {
            return responseData.map((responseObject: any) => {
              responseObject.filterTypeId = communicationsFilterEnums.FACILITY_FILTER_TYPE;
              return responseObject;
            });
          }
        }
      }
    }
  },
  components: {
    FilterComponent,
    TitleDropdownPicker,
    Personalization
  },
  mixins: [ MessageColumnMixin ],
  watch: {
    checkboxAllMessages() {
      (this as any).messages.forEach((message: UiMessage) => message.isChecked = this.checkboxAllMessages);
    },
    inboxMessages() {
      (this as any).setMessages(this.inboxMessages);
    },
    async providerId() {
      await this.setProviderFilter();
    },
    async facilityContactId() {
      await this.setFacilityContactFilter();
    },
    dropCount: function () {
      (this as any).moving = false;
    },
    async contactType() {
      await this.getAllCampaigns();
    }
  },
  async created () {
    if (this.providerId) {
      await this.setProviderFilter();
    } else if (this.facilityContactId) {
      await this.setFacilityContactFilter();
    } else {
      const savedFilters = this.$store.state.viewState[ViewStateType.COMMUNICATION_SELECTED_FILTERS] ? this.$store.state.viewState[ViewStateType.COMMUNICATION_SELECTED_FILTERS] : [];
      this.defaultFilters.push(...savedFilters);
      this.selectedCampaignId = this.$store.state.viewState[ViewStateType.COMMUNICATION_SELECTED_CAMPAIGN] ? this.$store.state.viewState[ViewStateType.COMMUNICATION_SELECTED_CAMPAIGN] : 0;
      this.searchInput = this.$store.state.viewState[ViewStateType.COMMUNICATION_SEARCH] ? this.$store.state.viewState[ViewStateType.COMMUNICATION_SEARCH] : '';
    }
    await this.getAllCampaigns();
  },
  computed : {
    filterTypesToRender (): any {
      // When contactTypeId changes, these change and the FilterComponent will emit 'filter-changed' which will reload the data
      if (this.contactType.id === 1) {
        return { ...this.filterTypes, ...this.providerFilterTypes }
      } else {
        return { ...this.filterTypes, ...this.facilityFilterTypes }
      }
    },
    inboxMessages(): ConversationSummary[] {
      return (this as any).$store.state.communication.inboxMessages;
    },
    hasContactId(): boolean {
      return Boolean(this.providerId || this.facilityContactId);
    },
    contactName(): string {
      let toReturn = '';
      if (this.contactType.id === ContactType.Provider) {
        toReturn = this.providerName;
      } else {
        toReturn = this.facilityContactName;
      }
      return toReturn
    },
    debounceGetMessages(): () => void {
      return debounce(this.getMessages, 150, { maxWait: 1000 });
    }
  },
  methods: {
    expandContactRow () {
      this.contactRowExpanded = !this.contactRowExpanded;
    },
    // DO NOT CALL THIS METHOD DIRECTLY, USE this.debounceGetMessages(); instead.
    async getMessages () {
      let currentSearchInput = this.searchInput;
      await this.$store.dispatch('viewState/updateViewState', {
        name: ViewStateType.COMMUNICATION_SEARCH,
        value: currentSearchInput
      });
      await this.$store.dispatch('communication/loadInboxMessages', {
        searchText: currentSearchInput,
        filtersSelected: this.filtersSelected,
        campaignId: this.selectedCampaignId,
        messageType: CommunicationType.SMS,
        contactType: this.contactType.id
      });
    },
    closeCampaigns() {
      this.showCampaigns = false;
    },
    async campaignSelected(campaignId: number) {
      this.closeCampaigns();
      const { name } = this.campaignOptions.find((campaign: any) => campaign.id === campaignId);
      this.selectedCampaignId = campaignId;
      this.selectedCampaign = name;
      await this.saveSelectedCampaign();
      await this.debounceGetMessages();
    },
    async saveSelectedCampaign() {
      await this.$store.dispatch('viewState/updateViewState', {
        name: ViewStateType.COMMUNICATION_SELECTED_CAMPAIGN,
        value: this.selectedCampaignId
      });
    },
    personalizationSelected(newMessage: string) {
      this.newMessage = newMessage;
    },
    async sendReply() {
      if (this.newMessage.length === 0) {
        return;
      }
      const sendReplyData = {
        message: this.newMessage,
        communicationTypeId: CommunicationType.SMS,
        providerIds: this.providerId ? [this.providerId] : [],
        facilityContactIds: this.facilityContactId ? [this.facilityContactId] : []
      };
      const responseData = await this.httpPost('/communications', 'Failed to send Reply', sendReplyData);
      if (responseData.sentMessageCount === 0) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error Sending Message',
          text: 'No message was sent. The Contact may have Opted-Out of messages.'
        });
      } else {
        this.newMessage = '';
        await this.debounceGetMessages();
      }
    },
    async filterChanged (filters: any[]) {
      this.filtersSelected = filters;
      await this.$store.dispatch('viewState/updateViewState', {
        name: ViewStateType.COMMUNICATION_SELECTED_FILTERS,
        value: JSON.parse(JSON.stringify(this.filtersSelected))
      });
      await this.debounceGetMessages();
    },
    async getAllCampaigns() {
      if (!this.contactType.id) {
        return;
      }
      this.campaignOptions = await (this as any).httpGet(`/communication-campaigns?messageType=${CommunicationType.SMS}&contactTypeId=${this.contactType.id}`, 'Failed to retrieve Conversations.') || [];
      this.campaignOptions.unshift({id: 0, name: 'All Messages'});
      if (this.selectedCampaignId) {
        const campaign = this.campaignOptions.find((campaign: any) => campaign.id === this.selectedCampaignId);
        if (campaign) {
          this.selectedCampaign = campaign.name;
          return;
        }
      }
      this.clearSelectedCampaign();
    },
    clearSelectedCampaign() {
      this.selectedCampaign = 'All Messages';
      this.selectedCampaignId = 0;
    },
    async setProviderFilter() {
      await this.setSingleFilter({
        filterTypeId: communicationsFilterEnums.PROVIDER_FILTER_TYPE,
        id: this.providerId,
        name: `${this.providerName}`,
      });
    },
    async setSingleFilter(filter: any) {
      const newFilters = [] as any[];
      this.clearSelectedCampaign();
      await this.saveSelectedCampaign();
      const allMessagesFilter = {
        filterTypeId: communicationsFilterEnums.STATUS_FILTER_TYPE,
        id: communicationsFilterEnums.ALL_MESSAGES_STATUS_FILTER_ID,
        name: 'All Messages',
      }
      newFilters.push(allMessagesFilter);
      newFilters.push(filter);
      // Only set once to not accidentally trigger multiple searches
      this.defaultFilters = newFilters;
    },
    async setFacilityContactFilter() {
      await this.setSingleFilter({
        filterTypeId: communicationsFilterEnums.FACILITY_CONTACT_FILTER_TYPE,
        id: this.facilityContactId,
        name: `${this.facilityContactName}`,
      });
    },
    async onDrop() {
      (this as any).moving = false
    }
  },
});



// WEBPACK FOOTER //
// ./src/components/communications/cards/MessageInboxCard.ts